import { buildApiUrl } from '@/lib/api/utils';

export const CSRF_HEADER_NAME = 'X-CSRF-Token';

const CSRF_ENDPOINT = '/csrf';

let cachedToken: string | null = null;
let inflightRequest: Promise<string> | null = null;

const isAbsoluteUrl = (value: string): boolean => /^https?:\/\//i.test(value);

const fetchCsrfToken = async (): Promise<string> => {
  const url = buildApiUrl(CSRF_ENDPOINT);
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch CSRF token (' + response.status + ')');
  }

  let token: string | null = null;
  const contentType = response.headers.get('content-type') ?? '';

  if (contentType.includes('application/json') || contentType === '') {
    const payload = (await response.json().catch(() => null)) as { token?: string } | null;
    if (payload && typeof payload.token === 'string' && payload.token.trim().length > 0) {
      token = payload.token;
    }
  }

  if (!token) {
    throw new Error('CSRF token response did not include a token');
  }

  cachedToken = token;
  return token;
};

const normaliseHeaders = (input?: HeadersInit): Record<string, string> => {
  if (!input) {
    return {};
  }

  if (input instanceof Headers) {
    const entries: Record<string, string> = {};
    input.forEach((value, key) => {
      entries[key] = value;
    });
    return entries;
  }

  if (Array.isArray(input)) {
    return input.reduce<Record<string, string>>((acc, entry) => {
      const key = String(entry[0]);
      const value = String(entry[1]);
      acc[key] = value;
      return acc;
    }, {});
  }

  return { ...(input as Record<string, string>) };
};

export const getCsrfToken = async (forceRefresh = false): Promise<string> => {
  if (!forceRefresh && cachedToken) {
    return cachedToken;
  }

  if (forceRefresh) {
    cachedToken = null;
  }

  if (!inflightRequest) {
    inflightRequest = fetchCsrfToken()
      .catch((error) => {
        cachedToken = null;
        throw error;
      })
      .finally(() => {
        inflightRequest = null;
      });
  }

  return inflightRequest;
};

export const invalidateCsrfToken = (): void => {
  cachedToken = null;
};

export const withCsrf = async (
  headers: HeadersInit = {},
  options: { forceRefresh?: boolean } = {},
): Promise<Record<string, string>> => {
  const merged = normaliseHeaders(headers);
  const token = await getCsrfToken(options.forceRefresh ?? false);
  merged[CSRF_HEADER_NAME] = token;
  return merged;
};

export const mergeHeaders = (
  base: HeadersInit | undefined,
  overrides: Record<string, string>,
): Record<string, string> => ({
  ...normaliseHeaders(base),
  ...overrides,
});

export const resolveRequestPath = (path: string): { original: string; api: string } => {
  const extractPathname = (value: string): string => {
    if (!value) {
      return '/';
    }

    if (isAbsoluteUrl(value)) {
      try {
        const url = new URL(value);
        return url.pathname || '/';
      } catch {
        return '/';
      }
    }

    const trimmed = value.split('?')[0];
    if (!trimmed) {
      return '/';
    }

    return trimmed.startsWith('/') ? trimmed : '/' + trimmed;
  };

  const original = extractPathname(path).toLowerCase();
  const api = original.startsWith('/api') ? original : original === '/' ? '/api' : '/api' + original;
  return { original, api };
};

export const withCsrfInit = async (
  init: RequestInit = {},
  options: { forceRefresh?: boolean } = {},
): Promise<RequestInit> => ({
  ...init,
  headers: await withCsrf(init.headers ?? {}, options),
});
